import { RefreshCw, Loader2, Sparkles, AlertCircle } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { SentimentChart } from './SentimentChart'
import { useThreadAnalysis } from '@/hooks/useAnalysis'
import { formatDateTime } from '@/utils/formatDate'

interface ThreadAnalysisProps {
  threadId: number
}

function getStatusMessage(status: string | null | undefined): { title: string; description: string } {
  switch (status) {
    case 'received':
      return {
        title: '분석 요청 접수됨',
        description: '분석 요청이 대기열에 추가되었습니다.',
      }
    case 'processing':
      return {
        title: '분석 생성 중',
        description: 'AI가 대화를 분석하고 있습니다. 잠시만 기다려주세요...',
      }
    default:
      return {
        title: '확인 중',
        description: '분석 상태를 확인하고 있습니다...',
      }
  }
}

export function ThreadAnalysis({ threadId }: ThreadAnalysisProps) {
  const { result, status, analyze, error, state } = useThreadAnalysis(threadId)

  // 분석 진행 중 상태 표시
  if (state === 'checking' || state === 'polling') {
    const { title, description } = getStatusMessage(status)

    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">스레드 분석</CardTitle>
        </CardHeader>
        <CardContent className="py-8">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="font-medium">{title}</p>
            <p className="text-sm text-muted-foreground text-center">{description}</p>
          </div>
        </CardContent>
      </Card>
    )
  }

  // 분석이 아직 시작되지 않은 경우
  if (state === 'idle') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">스레드 분석</CardTitle>
        </CardHeader>
        <CardContent className="py-8">
          <div className="flex flex-col items-center gap-3">
            <Sparkles className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">이 스레드는 아직 분석되지 않았습니다.</p>
            <Button onClick={() => analyze()} className="gap-2">
              <Sparkles className="h-4 w-4" />
              AI 분석 시작
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  // 에러 상태
  if (state === 'error') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">스레드 분석</CardTitle>
        </CardHeader>
        <CardContent className="py-8">
          <div className="flex flex-col items-center gap-3">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <p className="text-destructive font-medium">분석 실패</p>
            <p className="text-sm text-muted-foreground text-center">{error || '알 수 없는 오류가 발생했습니다.'}</p>
            <Button variant="outline" onClick={() => analyze(true)} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              다시 시도
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  // 분석 결과가 없는 경우
  if (!result) return null

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">스레드 분석</CardTitle>
        <div className="flex items-center gap-2">
          {result.cached && (
            <Badge variant="secondary">캐시됨</Badge>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => analyze(true)}
            title="새로 분석하기"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Summary */}
        <div>
          <h4 className="mb-2 font-medium">요약</h4>
          <p className="text-sm text-muted-foreground">{result.summary}</p>
        </div>

        {/* Topics */}
        <div>
          <h4 className="mb-2 font-medium">주제</h4>
          <div className="flex flex-wrap gap-2">
            {result.topics.map((topic) => (
              <Badge key={topic.topic} variant="outline">
                {topic.topic} ({topic.frequency})
              </Badge>
            ))}
          </div>
        </div>

        {/* Sentiment */}
        <div>
          <h4 className="mb-2 font-medium">감정</h4>
          <SentimentChart sentiment={result.sentiment} />
        </div>

        {/* Engagement */}
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">참여도</span>
          <Badge
            variant={
              result.engagement_level === 'high'
                ? 'success'
                : result.engagement_level === 'medium'
                ? 'warning'
                : 'secondary'
            }
          >
            {result.engagement_level}
          </Badge>
        </div>

        {/* User Persona */}
        {result.user_persona && (
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">사용자 페르소나</span>
            <Badge variant="outline">{result.user_persona}</Badge>
          </div>
        )}

        {/* Start/End Reasons */}
        {(result.start_reason || result.end_reason) && (
          <div className="space-y-2">
            {result.start_reason && (
              <div>
                <h4 className="mb-1 text-sm font-medium text-muted-foreground">시작 이유</h4>
                <p className="text-sm">{result.start_reason}</p>
              </div>
            )}
            {result.end_reason && (
              <div>
                <h4 className="mb-1 text-sm font-medium text-muted-foreground">종료 이유</h4>
                <p className="text-sm">{result.end_reason}</p>
              </div>
            )}
          </div>
        )}

        {/* Key Moments */}
        {result.key_moments.length > 0 && (
          <div>
            <h4 className="mb-2 font-medium">주요 순간</h4>
            <div className="space-y-2">
              {result.key_moments.map((moment) => (
                <div
                  key={moment.message_id}
                  className="rounded-lg bg-muted p-3"
                >
                  <p className="text-sm">{moment.description}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Sentiment: {moment.sentiment}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          분석됨: {formatDateTime(result.analyzed_at)}
          {result.message_count > 0 && ` • ${result.message_count} 메시지`}
        </p>
      </CardContent>
    </Card>
  )
}
